import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Clock, Building2, IndianRupee, TrendingUp, Loader2, GraduationCap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { fetchCourse, fetchColleges, fetchAuthor } from '../lib/api';
import { STREAMS } from '../data/mock';
import Seo from '../components/Seo';
import { buildCourseSchema } from '../lib/schema';
import NotFound from './NotFound';

const CourseDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState(undefined);
  const [related, setRelated] = useState([]);
  const [author, setAuthor] = useState(null);

  useEffect(() => {
    setState(undefined);
    setAuthor(null);
    fetchCourse(id)
      .then((res) => {
        setState(res);
        const src = res.kind === 'parent' ? res.parent : res.data;
        if (src?.author_id) fetchAuthor(src.author_id).then(setAuthor).catch(() => {});
        if (res.parent?.stream) fetchColleges({ stream: res.parent.stream }).then((list) => setRelated(list.slice(0, 4))).catch(() => {});
      })
      .catch(() => setState(null));
  }, [id]);

  if (state === undefined) return <div className="min-h-screen grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-teal-600" /></div>;
  if (state === null) return <NotFound />;

  const { kind, data, parent, subpages } = state;
  const isSub = kind === 'sub';
  const course = parent;
  const activeSlug = isSub ? data.slug : parent.slug;
  const seoSrc = data;
  const streamMeta = STREAMS.find((s) => s.key === course.stream);

  return (
    <div className="bg-slate-50 min-h-screen">
      <Seo
        title={seoSrc.meta_title || `${isSub ? data.tab_label + ' - ' : ''}${course.name} Course | Learndunia`}
        description={seoSrc.meta_description || course.description}
        keywords={seoSrc.meta_keywords}
        image={seoSrc.og_image}
        canonical={seoSrc.canonical_url || `https://learndunia.com/courses/${activeSlug}`}
        jsonLd={buildCourseSchema(course)}
      />

      <div className="bg-gradient-to-br from-teal-600 to-teal-700 text-white">
        <div className="max-w-6xl mx-auto px-4 py-10">
          <button onClick={() => navigate(-1)} className="text-sm text-teal-50 hover:text-white inline-flex items-center gap-1 mb-6">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          {streamMeta && (
            <Badge className="bg-white/20 hover:bg-white/20 text-white border-white/30 mb-3">{streamMeta.label}</Badge>
          )}
          <h1 className="text-3xl md:text-5xl font-bold" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{course.name}</h1>
          <p className="text-teal-50 mt-1">{course.full}</p>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-8">
            {[
              { i: Clock, l: 'Duration', v: course.duration },
              { i: IndianRupee, l: 'Avg Fees', v: course.avgFees },
              { i: TrendingUp, l: 'Avg Salary', v: course.avgSalary },
              { i: Building2, l: 'Colleges', v: `${course.colleges}+` },
            ].map((s, i) => (
              <div key={i} className="bg-white/10 backdrop-blur border border-white/20 rounded-xl p-3">
                <s.i className="w-5 h-5 text-orange-300" />
                <div className="text-[11px] uppercase text-teal-100 mt-2">{s.l}</div>
                <div className="font-semibold">{s.v}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-10">
        {/* Tabs */}
        <div className="mb-6 bg-white border border-slate-200 rounded-xl p-1 overflow-x-auto flex gap-1">
          <Link to={`/courses/${course.slug}`}
            className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${!isSub ? 'bg-teal-600 text-white' : 'text-slate-700 hover:bg-slate-100'}`}>
            Overview
          </Link>
          {subpages.map((sp) => (
            <Link key={sp.id} to={`/courses/${sp.slug}`}
              className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${isSub && sp.slug === activeSlug ? 'bg-teal-600 text-white' : 'text-slate-700 hover:bg-slate-100'}`}>
              {sp.tab_label}
            </Link>
          ))}
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-2xl p-6">
          {isSub ? (
            <>
              <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{data.tab_label}</h2>
              {data.content ? <div className="prose prose-slate max-w-none mt-4" dangerouslySetInnerHTML={{ __html: data.content }} /> : <p className="text-slate-500 mt-3">Content coming soon.</p>}
            </>
          ) : (
            <>
              <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Overview</h2>
              <p className="mt-3 text-slate-700 leading-relaxed">{course.description}</p>
              {course.content && (
                <div className="prose prose-slate max-w-none mt-6" dangerouslySetInnerHTML={{ __html: course.content }} />
              )}
            </>
          )}
          {author && (
            <div className="mt-8 pt-6 border-t border-slate-100 flex items-center gap-3">
              <img src={author.avatar} alt={author.name} className="w-12 h-12 rounded-full object-cover" />
              <div>
                <div className="text-xs text-slate-500">Written &amp; fact-checked by</div>
                <Link to={`/author/${author.slug}`} className="font-semibold text-slate-900 hover:text-teal-700">{author.name}</Link>
                <div className="text-xs text-slate-500">{author.designation}</div>
              </div>
            </div>
          )}
        </div>

        <aside className="space-y-4">
          <div className="bg-white border border-slate-200 rounded-2xl p-5">
            <h3 className="font-semibold text-slate-900 mb-3" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Top Colleges for {course.name}</h3>
            <div className="space-y-2">
              {related.length === 0 && <p className="text-sm text-slate-500">No colleges listed yet.</p>}
              {related.map((c) => (
                <Link key={c.id} to={`/colleges/${c.slug || c.id}`} className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50 group">
                  <img src={c.logo} alt="" className="w-9 h-9 rounded object-cover" />
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-slate-900 group-hover:text-teal-700 truncate">{c.short || c.name}</div>
                    <div className="text-xs text-slate-500 truncate">{c.city}, {c.state}</div>
                  </div>
                </Link>
              ))}
            </div>
          </div>

          <div className="bg-gradient-to-br from-orange-500 to-orange-600 text-white rounded-2xl p-5">
            <GraduationCap className="w-8 h-8 mb-2" />
            <h3 className="font-bold text-lg" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Need help choosing?</h3>
            <p className="text-orange-50 text-sm mt-1">Book a free counselling session with our expert.</p>
            <Button className="w-full bg-slate-900 hover:bg-slate-800 text-white mt-3">Request Callback</Button>
          </div>
        </aside>
        </div>
      </div>
    </div>
  );
};

export default CourseDetail;
